import type { SecretId, SecretListing } from "@repo/app-types/entities/secret";
import {
  checkCodeToolCanSendSecret,
  codeToolMaxSecretCount,
} from "@repo/app-types/entities/tools/code";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import {
  IconAlertTriangle,
  IconPencil,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import { useState, type FC } from "react";
import { DialogTrigger } from "react-aria-components";
import { ManageSecretDialog } from "../../secrets/components/manageSecretDialog.js";
import { SecretTable } from "../../secrets/components/secretTable.js";

interface CodeToolSecretsEditorProps {
  /** The secrets the tool references, saved or not. */
  secrets: readonly SecretListing[];
  /** The tool's allowed domains as the form has them, saved or not. */
  toolDomains: readonly string[];
  onChange: (secrets: SecretListing[]) => void;
}

export const CodeToolSecretsEditor: FC<CodeToolSecretsEditorProps> = (
  props,
) => {
  const { secrets, toolDomains, onChange } = props;

  const [editingId, setEditingId] = useState<SecretId>();
  const editingSecret = secrets.find(({ id }) => id === editingId);

  const handleAdd = (added: SecretListing) => {
    onChange([...secrets, added]);
  };

  const handleRemove = (removed: SecretListing) => {
    onChange(secrets.filter(({ id }) => id !== removed.id));
  };

  const handleEdit = (edited: SecretListing) => {
    onChange(
      secrets.map((secret) => (secret.id === edited.id ? edited : secret)),
    );
  };

  const findDomainsError = (allowedDomains: readonly string[]) =>
    checkCodeToolCanSendSecret(allowedDomains, toolDomains)
      ? undefined
      : "Choose at least one of the tool's allowed domains to send this secret to.";
  const domainsDescription =
    toolDomains.length === 0
      ? "Add an allowed domain to the tool first."
      : undefined;

  const isAtLimit = secrets.length >= codeToolMaxSecretCount;
  const addButton = (
    <Button
      variant={ButtonVariant.Secondary}
      size={Size.SMALL}
      icon={<IconPlus className="h-4 w-4" />}
      isDisabled={isAtLimit}
    >
      Add secret
    </Button>
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Label size={Size.SMALL}>Secrets</Label>
        {isAtLimit ? (
          // A disabled button fires no hover events, so the tooltip hangs off a wrapper.
          <TooltipTrigger>
            <TooltipHoverTarget shape="rounded">{addButton}</TooltipHoverTarget>
            <SimpleTooltip>
              A tool can have up to {codeToolMaxSecretCount} secrets.
            </SimpleTooltip>
          </TooltipTrigger>
        ) : (
          <DialogTrigger>
            {addButton}
            <ManageSecretDialog
              secret={undefined}
              excludedSecrets={secrets}
              suggestedDomains={toolDomains}
              domainsDescription={domainsDescription}
              findDomainsError={findDomainsError}
              onSubmit={handleAdd}
            />
          </DialogTrigger>
        )}
      </div>

      <SecretTable
        aria-label="Secrets"
        secrets={secrets}
        emptyMessage="No secrets yet."
        renderAccessory={(secret) =>
          checkCodeToolCanSendSecret(
            secret.allowed_domains,
            toolDomains,
          ) ? null : (
            <TooltipTrigger>
              <TooltipHoverTarget shape="rounded">
                <IconAlertTriangle
                  aria-hidden
                  className="size-4 text-destructive"
                />
              </TooltipHoverTarget>
              <SimpleTooltip>
                This secret isn&apos;t allowed to send to any of the tool&apos;s
                allowed domains.
              </SimpleTooltip>
            </TooltipTrigger>
          )
        }
        renderActions={(secret) => (
          <>
            <IconButton
              icon={<IconPencil className="h-4 w-4" />}
              variant={ButtonVariant.Secondary}
              accessibleLabel={`Edit ${secret.name}`}
              onClick={() => setEditingId(secret.id)}
            />
            <IconButton
              icon={<IconX className="h-4 w-4" />}
              variant={ButtonVariant.Secondary}
              accessibleLabel={`Remove ${secret.name}`}
              onClick={() => handleRemove(secret)}
            />
          </>
        )}
      />

      <p className="text-sm text-muted-foreground">
        Values the script reads as <code>secrets.NAME</code>, such as API keys.
        Other tools can use the same secret. Each can only be sent to its own
        domains, and its value can&apos;t be read back once saved.
      </p>

      {/* An edit is saved at once, and replaces the secret's row in the form. */}
      <ManageSecretDialog
        isOpen={editingSecret !== undefined}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setEditingId(undefined);
          }
        }}
        secret={editingSecret}
        suggestedDomains={toolDomains}
        domainsDescription={domainsDescription}
        findDomainsError={findDomainsError}
        onSubmit={handleEdit}
      />
    </div>
  );
};
